import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { StudentContentContextResponse } from "../../Platform/Common/types";

interface StudentContextType {
  studentContext: StudentContentContextResponse | null;
  setStudentContext: React.Dispatch<React.SetStateAction<StudentContentContextResponse | null>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const StudentContext = createContext<StudentContextType | undefined>(undefined);

export const StudentContextProvider = ({
  children,
  accountKey,                       // NEW: identifies the logged-in student (null = logged out)
}: {
  children: React.ReactNode;
  accountKey: string | null;
}) => {
  // Data is stored together with the account it was loaded for
  const [stored, setStored] = useState<{
    key: string | null;
    data: StudentContentContextResponse | null;
  }>({ key: null, data: null });
  const [storedLoading, setStoredLoading] = useState(true);

  // Wipe on logout so the same student re-logging in also gets a fresh load
  useEffect(() => {
    if (!accountKey) {
      setStored({ key: null, data: null });
      setStoredLoading(true);
    }
  }, [accountKey]);

  // Stamps whatever gets stored with the account it was set for
  const setStudentContext = useCallback<
    React.Dispatch<React.SetStateAction<StudentContentContextResponse | null>>
  >(
    (value) =>
      setStored((prev) => ({
        key: accountKey,
        data:
          typeof value === "function"
            ? value(prev.key === accountKey ? prev.data : null)
            : value,
      })),
    [accountKey]
  );

  // Derived during render, so there is no window where B can see A's data
  const isCurrent = stored.key === accountKey;
  const studentContext = isCurrent ? stored.data : null;
  const loading = isCurrent ? storedLoading : true;

  return (
    <StudentContext.Provider
      value={{ studentContext, setStudentContext, loading, setLoading: setStoredLoading }}
    >
      {children}
    </StudentContext.Provider>
  );
};

// useStudentContext unchanged

export const useStudentContext = () => {

  const context = useContext(StudentContext);

  if (!context) {
    throw new Error(
      "useStudentContext must be used inside StudentContextProvider"
    );
  }

  return context;
};